import React, { useEffect, useRef, useState } from 'react';
import { useInView } from 'framer-motion';

/**
 * AnimatedCounter
 * Zählt eine Zahl von 0 zum Zielwert hoch, sobald die Komponente sichtbar ist.
 *
 * Props:
 *  - value: Zahl oder String (z.B. 300, "300+", 19)
 *  - duration: Animationsdauer in ms (Default 1800)
 *  - className: optionale CSS-Klassen
 */
export const AnimatedCounter = ({ value, duration = 1800, className = '' }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '0px 0px -50px 0px' });
  const [display, setDisplay] = useState('0');

  // Wert in numerischen Teil und Suffix zerlegen (z.B. "300+" => 300 + "+")
  const valueStr = String(value ?? '0');
  const match = valueStr.match(/^(\d+)(.*)$/);
  const targetNum = match ? parseInt(match[1], 10) : 0;
  const suffix = match ? match[2] : '';

  useEffect(() => {
    if (!inView) return;
    if (targetNum === 0) {
      setDisplay(`0${suffix}`);
      return;
    }

    let raf;
    const start = performance.now();

    const tick = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // easeOutQuad
      const eased = 1 - (1 - progress) * (1 - progress);
      const current = Math.round(eased * targetNum);
      setDisplay(`${current}${suffix}`);
      if (progress < 1) {
        raf = requestAnimationFrame(tick);
      }
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, targetNum, suffix, duration]);

  return (
    <span ref={ref} className={className} data-testid="animated-counter">
      {display}
    </span>
  );
};

export default AnimatedCounter;
